import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import { formatCurrency as fmtCAD } from '../../../utils/formatCurrency';
import { signedExtraAmount } from '../../../utils/extraSign';
import useEscapeKey from '../../../hooks/useEscapeKey';

const ApproveExtraModal = ({ isOpen, extra, lines, onCancel, onConfirm, loading }) => {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  // Nombre de lignes internes de l'extra. Il faut le retenir : sans lui, on ne
  // peut pas distinguer « aucun sous-traitant mais du travail interne » (cas B,
  // approuvable sans avenant) de « aucune ligne exploitable » (cas C, bloque).
  const [internalCount, setInternalCount] = useState(0);
  // Ne pas peindre la modale en rouge des l'ouverture : une erreur n'apparait
  // qu'apres une tentative d'envoi, ou apres qu'on a quitte le champ en le
  // laissant vide.
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Carte de chaque groupe, par subcontractId : sert a ramener le premier
  // groupe fautif a l'ecran quand l'envoi est bloque.
  const groupRefs = useRef({});

  useEffect(() => {
    if (!isOpen || !lines) return;

    // Filter: exclude internal lines (is_internal = true OR subcontract_id = null)
    const subcontractorLines = lines?.filter(
      l => !l?.is_internal && l?.subcontract_id != null
    );

    // Group by subcontract_id
    const grouped = {};
    for (const line of subcontractorLines) {
      const key = line?.subcontract_id;
      if (!grouped?.[key]) {
        grouped[key] = {
          subcontractId: key,
          subcontractorName:
            line?.subcontractorName ||
            line?.subcontractor?.companyName ||
            line?.subcontractor?.company_name ||
            key,
          lines: [],
          notes: '',
        };
      }
      grouped?.[key]?.lines?.push({ ...line, checked: true });
    }

    setGroups(Object.values(grouped));
    setInternalCount(lines?.filter(l => l?.is_internal === true)?.length || 0);
    setTouched({});
    setSubmitAttempted(false);
  }, [isOpen, lines]);

  const handleToggleLine = (subcontractId, lineId) => {
    setGroups(prev =>
      prev?.map(g =>
        g?.subcontractId === subcontractId
          ? {
              ...g,
              lines: g?.lines?.map(l =>
                l?.id === lineId ? { ...l, checked: !l?.checked } : l
              ),
            }
          : g
      )
    );
  };

  const handleNotesChange = (subcontractId, value) => {
    setGroups(prev =>
      prev?.map(g =>
        g?.subcontractId === subcontractId ? { ...g, notes: value } : g
      )
    );
  };

  const getGroupTotal = (group) =>
    group?.lines?.filter(l => l?.checked)?.reduce((sum, l) => sum + (Number(l?.amount) || 0), 0);

  const grandTotal = groups?.reduce((sum, g) => sum + getGroupTotal(g), 0);

  // Un credit s'affiche avec son signe : c'est le montant que l'avenant portera.
  // Les sommes elles-memes restent positives, comme les lignes en base.
  const isCredit = extra?.type === 'credit';
  const fmtAmt = (val) => {
    const signed = signedExtraAmount(extra, val);
    return signed < 0 ? `−${fmtCAD(-signed)}` : fmtCAD(signed);
  };

  /**
   * Un groupe n'EXIGE une description que s'il produit reellement un avenant,
   * c'est-a-dire s'il a au moins une ligne cochee. Un groupe dont on a tout
   * decoche est une facon legitime de sauter un sous-traitant : il ne doit
   * jamais bloquer le formulaire.
   */
  const needsDescription = (group) =>
    group?.lines?.some(l => l?.checked) && !group?.notes?.trim();

  /** L'erreur ne s'affiche qu'une fois le champ touche, ou apres un envoi tente. */
  const showErrorFor = (group) =>
    needsDescription(group) && (submitAttempted || touched?.[group?.subcontractId]);

  // ── Les trois cas, explicites ──────────────────────────────────────────────
  // A : au moins un sous-traitant     -> avenants puis approbation
  // B : aucun sous-traitant, du interne -> approbation SANS avenant
  // C : rien d'exploitable             -> bloque, il faut editer l'extra
  const hasSubcontractorLines = groups?.length > 0;
  const isInternalOnly = !hasSubcontractorLines && internalCount > 0;
  const hasNothingUsable = !hasSubcontractorLines && internalCount === 0;

  // Lignes reellement cochees, tous groupes confondus. Tout decocher ne doit
  // PAS approuver : ca produisait un extra approuve sans le moindre avenant.
  const totalSelectedLineIds = groups?.reduce(
    (n, g) => n + (g?.lines?.filter(l => l?.checked)?.length || 0),
    0
  );

  const handleConfirm = () => {
    // Cas B : aucun sous-traitant n'est engage, donc aucun avenant a creer.
    // On le dit explicitement a l'appelant plutot que de lui envoyer un
    // tableau vide qu'il pourrait confondre avec « rien de coche ».
    if (isInternalOnly) {
      onConfirm([], { internalOnly: true });
      return;
    }

    // Le bouton reste actif : c'est le clic qui revele d'un coup toutes les
    // descriptions manquantes. Rien n'est cree tant qu'il en manque une.
    const firstOffending = groups?.find(needsDescription);
    if (firstOffending) {
      setSubmitAttempted(true);
      groupRefs?.current?.[firstOffending?.subcontractId]?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest',
      });
      return;
    }

    const selections = groups?.map(g => ({
        subcontractId: g?.subcontractId,
        lineIds: g?.lines?.filter(l => l?.checked)?.map(l => l?.id),
        notes: g?.notes,
      }))?.filter(s => s?.lineIds?.length > 0);

    onConfirm(selections, { internalOnly: false });
  };

  useEscapeKey(onCancel, !!isOpen && !loading);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
              <Icon name="FileCheck" size={18} className="text-orange-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 leading-snug truncate">
                {t('extras.approve.title')}
              </h2>
              <p className="text-[11px] text-gray-500 truncate">
                {t('extras.approve.subtitle')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {isInternalOnly ? (
            /* CAS B — que du travail interne : aucun sous-traitant n'est
               engage, donc aucun avenant n'a de sens. */
            <div className="text-center py-6 px-4">
              <Icon name="Home" size={22} className="text-primary mx-auto mb-2" />
              <p className="text-sm font-semibold text-gray-900">
                {t('extra.internalOnlyTitle')}
              </p>
              <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                {t('extra.internalOnlyBody')}
              </p>
            </div>
          ) : hasNothingUsable ? (
            /* CAS C — aucune ligne exploitable (anciennes lignes « Tous »).
               On n'approuve PAS en silence : il faut d'abord editer l'extra. */
            <div className="text-center py-6 px-4">
              <Icon name="AlertTriangle" size={22} className="text-amber-500 mx-auto mb-2" />
              <p className="text-xs text-gray-600 leading-relaxed">
                {t('extra.noLinesError')}
              </p>
            </div>
          ) : (
            groups?.map(group => {
              const groupTotal = getGroupTotal(group);
              return (
                <div
                  key={group?.subcontractId}
                  ref={el => { groupRefs.current[group?.subcontractId] = el; }}
                  className="border border-gray-200 rounded-xl overflow-hidden"
                >
                  {/* Group header */}
                  <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <Icon name="Building2" size={14} className="text-gray-400" />
                      <span className="text-xs font-semibold text-gray-800">
                        {group?.subcontractorName}
                      </span>
                    </div>
                    <span className={`text-xs font-semibold ${isCredit ? 'text-red-600' : 'text-primary'}`}>
                      {fmtAmt(groupTotal)}
                    </span>
                  </div>
                  {/* Lines */}
                  <div className="divide-y divide-gray-100">
                    {group?.lines?.map(line => (
                      <label
                        key={line?.id}
                        className="flex items-start gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={line?.checked}
                          onChange={() =>
                            handleToggleLine(group?.subcontractId, line?.id)
                          }
                          className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-2 focus:ring-primary/20 cursor-pointer flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-700 leading-snug">
                            {line?.description || '—'}
                          </p>
                        </div>
                        <span
                          className={`text-xs font-medium flex-shrink-0 ${
                            !line?.checked ? 'text-gray-400 line-through' : isCredit ? 'text-red-600' : 'text-gray-900'
                          }`}
                        >
                          {fmtAmt(line?.amount)}
                        </span>
                      </label>
                    ))}
                  </div>
                  {/* Description — obligatoire des qu'une ligne du groupe est cochee */}
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('extras.approve.description')}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={group?.notes}
                      onChange={e =>
                        handleNotesChange(group?.subcontractId, e?.target?.value)
                      }
                      onBlur={() =>
                        setTouched(prev => ({ ...prev, [group?.subcontractId]: true }))
                      }
                      rows={2}
                      className={`w-full text-xs border rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors bg-white ${
                        showErrorFor(group)
                          ? 'border-red-400 focus:border-red-400'
                          : 'border-gray-200 focus:border-primary'
                      }`}
                      placeholder={t('extras.approve.description')}
                    />
                    {/* L'erreur vit SOUS le champ du groupe fautif : l'utilisateur
                        doit voir DE QUEL sous-traitant il s'agit. */}
                    {showErrorFor(group) && (
                      <p className="text-xs text-red-500 mt-1">
                        {t('extras.approve.descriptionRequired')}
                      </p>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{t('extras.approve.total')}:</span>
            <span className={`text-sm font-bold ${isCredit ? 'text-red-600' : 'text-gray-900'}`}>{fmtAmt(grandTotal)}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t('extras.approve.cancel')}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={
                loading ||
                hasNothingUsable ||
                // Cas A : tout decocher ne doit plus laisser passer le clic.
                (!isInternalOnly && totalSelectedLineIds === 0)
              }
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60"
            >
              {loading ? (
                <Icon name="Loader2" size={13} className="animate-spin" />
              ) : (
                <Icon name="CheckCircle" size={13} />
              )}
              {isInternalOnly
                ? t('extra.approveWithoutAvenant')
                : t('extras.approve.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApproveExtraModal;
